import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { isErrorGloballyHandled } from '../../../global-message';
import { GlobalMessageType } from '../../../global-message/models/global-message.model';
import { GlobalMessageActions } from '../../../global-message/store/actions/index';
import { ErrorModel } from '../../../model/misc.model';
import { normalizeHttpError } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { UserProfileConnector } from '../../connectors/user-profile.connector';
import { UserActions } from '../actions/index';

@Injectable()
export class ResetPasswordEffects {
  resetPassword$: Observable<UserActions.ResetPasswordSuccess | UserActions.ResetPasswordFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ResetPasswordActionTypes.ResetPassword),
      map((action: UserActions.ResetPassword) => action.payload),
      switchMap(({ token, password }) => {
        return this.userProfileConnector.resetPassword(token, password).pipe(
          map(() => new UserActions.ResetPasswordSuccess()),
          catchError((error) => of(new UserActions.ResetPasswordFail(normalizeHttpError(error))))
        );
      })
    )
  );

  handleSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ResetPasswordActionTypes.ResetPasswordSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: { key: 'password.reset.updatePasswordSuccess_message' },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  handleFailure$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ResetPasswordActionTypes.ResetPasswordFail),
      map((action: UserActions.ResetPasswordFail) => action.payload),
      switchMap((payload) => {
        if (!isErrorGloballyHandled(payload?.status)) {
          if (Array.isArray(payload?.details)) {
            const messageActionsArray: GlobalMessageActions.AddMessage[] = payload.details
              .filter((errorDetail: ErrorModel) => errorDetail.message)
              .map((errorDetail: ErrorModel) => {
                return new GlobalMessageActions.AddMessage({
                  text: { raw: errorDetail.message },
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                });
              });

            if (messageActionsArray.length) {
              return messageActionsArray;
            }
          }
          return [
            new GlobalMessageActions.AddMessage({
              text: { key: 'password.reset.updatePasswordFailure_message' },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            }),
          ];
        } else {
          return [new AppActions.NoOpAction()];
        }
      })
    )
  );

  constructor(private actions$: Actions, private userProfileConnector: UserProfileConnector) {}
}
